import type { ComplianceFilters } from '../services/compliance.service';
import { VALID_ISSUE_TYPES } from './compliance-counts';

/**
 * Maps URL issue type parameter to compliance filters
 * This allows URL-based navigation while preserving client-side filtering performance
 */

/**
 * Centralized URL building function for compliance navigation
 * Handles client/entity context and provides fallbacks for missing data
 */
export function buildComplianceUrl(
  issueType: string, 
  clientId: string | null, 
  entityId: string | 'all' | null
): string {
  if (!clientId) {
    console.warn('buildComplianceUrl: No clientId provided, redirecting to dashboard');
    return '/dashboard'; // Fallback if no client selected
  }
  
  // Validate issue type and use default if invalid
  const validatedIssueType = isValidIssueType(issueType) ? issueType : getDefaultIssueType();
  
  // Handle entity parameter - empty string for 'all entities'
  const entityParam = entityId && entityId !== 'all' ? entityId : '';
  
  return `/compliance/${validatedIssueType}/${clientId}/${entityParam}`;
}
export function getFiltersFromIssueType(issueType: string): ComplianceFilters {
  // Default filter state (shows all issues)
  const defaultFilters: ComplianceFilters = {
    types: [],
    severities: [],
    status: [],
    entityIds: []
  };

  // Map issue type to specific filters (XeroConnectVr-aligned single letters)
  switch (issueType?.toLowerCase()) {
    // Individual issue types
    case 'a':
      return { ...defaultFilters, types: ['A'] };
    case 'b':
      return { ...defaultFilters, types: ['B'] };
    case 'c':
      return { ...defaultFilters, types: ['C'] };
    case 'd':
      return { ...defaultFilters, types: ['D'] };
    case 'e':
      return { ...defaultFilters, types: ['E'] };
    case 'f':
      return { ...defaultFilters, types: ['F'] };
    case 'g':
      return { ...defaultFilters, types: ['G'] };
    case 'h':
      return { ...defaultFilters, types: ['H'] };
    case 'i':
      return { ...defaultFilters, types: ['I'] };
    case 'j':
      return { ...defaultFilters, types: ['J'] };
    case 'k':
      return { ...defaultFilters, types: ['K'] };

    // Grouped issue types
    case 'vat':
      return { ...defaultFilters, types: ['A'] };
    case 'duplicates':
      return { ...defaultFilters, types: ['C', 'D'] };
    case 'gl':
      return { ...defaultFilters, types: ['E', 'J'] };

    // Default: show all issues
    case 'all':
    default:
      return defaultFilters;
  }
}

/**
 * Gets the page title based on issue type
 */
export function getPageTitleFromIssueType(issueType: string): string {
  switch (issueType?.toLowerCase()) {
    case 'a':
      return 'VAT Issues';
    case 'b':
      return 'Mixed VAT';
    case 'c':
      return 'Duplicate Bills';
    case 'd':
      return 'Spend vs Bill';
    case 'e':
      return 'Mixed GL';
    case 'f':
      return 'Aged Payables';
    case 'g':
      return 'Aged Receivables';
    case 'h':
      return 'Unreconciled Bank Transactions';
    case 'i':
      return 'Serial Checker';
    case 'j':
      return 'Same GL';
    case 'k':
      return 'Clearing / Control';
    case 'vat':
      return 'VAT Issues';
    case 'duplicates':
      return 'Duplicate Issues';
    case 'gl':
      return 'GL Issues';
    case 'all':
    default:
      return 'All Compliance Issues';
  }
}

/**
 * Gets the breadcrumb text based on issue type (without issue codes for cleaner UI)
 */
export function getBreadcrumbFromIssueType(issueType: string): string {
  switch (issueType?.toLowerCase()) {
    case 'a':
      return 'VAT Issues';
    case 'b':
      return 'Mixed VAT';
    case 'c':
      return 'Duplicate Bills';
    case 'd':
      return 'Spend vs Bill';
    case 'e':
      return 'Mixed GL';
    case 'f':
      return 'Aged Payables';
    case 'g':
      return 'Aged Receivables';
    case 'h':
      return 'Unreconciled Bank Transactions';
    case 'i':
      return 'Serial Checker';
    case 'j':
      return 'Same GL';
    case 'k':
      return 'Clearing / Control';
    case 'vat':
      return 'VAT Issues';
    case 'duplicates':
      return 'Duplicate Issues';
    case 'gl':
      return 'GL Issues';
    case 'all':
    default:
      return 'All Issues';
  }
}

/**
 * Validates if an issue type is supported
 */
export function isValidIssueType(issueType: string): boolean {
  const validTypes = [
    'all', 'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k',
    'vat', 'duplicates', 'gl'
  ];
  return validTypes.includes(issueType?.toLowerCase() || '');
}

/**
 * Gets default issue type for fallback
 */
export function getDefaultIssueType(): string {
  return 'all';
}